import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api/client';
import { UserType } from '@/types/inde_menu';

// ============================================================================
// QUERIES
// ============================================================================

export const useUserById = (userId?: string) => {
  return useQuery({
    queryKey: ['users', userId],
    queryFn: async (): Promise<UserType> => {
      const { data } = await apiClient.get(`/users/${userId}`);
      return data;
    },
    enabled: !!userId,
  });
};

export const useUsers = () => {
  return useQuery({
    queryKey: ['users'],
    queryFn: async (): Promise<UserType[]> => {
      const { data } = await apiClient.get('/users');
      return data;
    },
  });
};

export const useWaitersByRestaurant = (restaurantId?: string) => {
  return useQuery({
    queryKey: ['waiters', restaurantId],
    queryFn: async (): Promise<UserType[]> => {
      const { data } = await apiClient.get(`/restaurants/${restaurantId}/waiters`);
      return data;
    },
    enabled: !!restaurantId,
  });
};

// ============================================================================
// MUTATIONS
// ============================================================================

export const useAddUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (userData: Partial<UserType>) => {
      const { data } = await apiClient.post('/users', userData);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['waiters'] });
    },
  });
};

export const useUpdateUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, updates }: { id: string | number; updates: Partial<UserType> & Record<string, any> }) => {
      const { data } = await apiClient.put(`/users/${id}`, updates);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['waiters'] });
    },
  });
};

export const useDeleteUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string | number) => {
      const { data } = await apiClient.delete(`/users/${userId}`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['waiters'] });
    },
  });
};

export const useResetPassword = () => {
  return useMutation({
    mutationFn: async (userId: string | number) => {
      const { data } = await apiClient.post(`/users/${userId}/reset-password`);
      return data;
    },
  });
};

export const useBlockUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string | number) => {
      const { data } = await apiClient.post(`/users/${userId}/block`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};

// ============================================================================
// STANDALONE FUNCTIONS (for imperative use in useEffect, etc.)
// ============================================================================

export const fetchUserById = async (userId: string): Promise<UserType> => {
  const { data } = await apiClient.get(`/users/${userId}`);
  return data;
};

export const fetchWaitersByRestaurantId = async (restaurantId: string): Promise<UserType[]> => {
  const { data } = await apiClient.get(`/restaurants/${restaurantId}/waiters`);
  return data;
};
